import { useState } from 'react';

export interface DeliveryData {
  method: 'courier' | 'pickup' | 'post';
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  apartment: string;
  comment: string;
  pickupPoint: string;
}

interface Props {
  data: DeliveryData;
  onChange: (data: DeliveryData) => void;
  onNext: () => void;
}

const DELIVERY_METHODS = [
  {
    id: 'courier' as const,
    label: 'Курьером',
    desc: 'Доставка до двери за 2–4 часа',
    price: 'от 199 ₽',
    freeFrom: 'Бесплатно от 3 000 ₽',
    icon: 'ri-e-bike-line',
    color: '#ff2d78',
  },
  {
    id: 'pickup' as const,
    label: 'Самовывоз',
    desc: 'Из аптеки или пункта выдачи',
    price: 'Бесплатно',
    freeFrom: 'Готово через 1 час',
    icon: 'ri-store-2-line',
    color: '#39ff14',
  },
  {
    id: 'post' as const,
    label: 'Почта России',
    desc: 'Доставка по всей России',
    price: 'от 299 ₽',
    freeFrom: '5–14 рабочих дней',
    icon: 'ri-mail-send-line',
    color: '#00f5ff',
  },
];

const PICKUP_POINTS = [
  'ул. Ленина, 12 — Аптека NeonPharma (пн–вс 8:00–22:00)',
  'пр. Мира, 45 — Аптека NeonPharma (круглосуточно)',
  'ул. Садовая, 8 — Пункт выдачи (пн–пт 9:00–21:00)',
  'ТЦ «Галерея», 3 этаж — Аптека NeonPharma (10:00–22:00)',
];

const Field = ({
  label, value, onChange, placeholder, type = 'text', required = false, half = false,
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean; half?: boolean;
}) => (
  <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
    <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
      {label}{required && <span style={{ color: '#ff2d78' }}> *</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none transition-all"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
      onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
    />
  </div>
);

const DeliveryForm = ({ data, onChange, onNext }: Props) => {
  const [errors, setErrors] = useState<Partial<Record<keyof DeliveryData, string>>>({});

  const set = (key: keyof DeliveryData) => (val: string) => onChange({ ...data, [key]: val });

  const validate = () => {
    const e: typeof errors = {};
    if (!data.firstName.trim()) e.firstName = 'Введите имя';
    if (!data.lastName.trim()) e.lastName = 'Введите фамилию';
    if (!data.phone.trim()) e.phone = 'Введите телефон';
    if (!data.email.trim()) e.email = 'Введите email';
    if (data.method === 'courier' || data.method === 'post') {
      if (!data.city.trim()) e.city = 'Введите город';
      if (!data.address.trim()) e.address = 'Введите адрес';
    }
    if (data.method === 'pickup' && !data.pickupPoint) e.pickupPoint = 'Выберите пункт выдачи';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validate()) onNext(); };

  return (
    <div className="space-y-6">
      {/* Delivery method */}
      <div>
        <h3 className="font-orbitron font-bold text-sm text-white mb-4">Способ доставки</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {DELIVERY_METHODS.map(m => (
            <button
              key={m.id}
              onClick={() => onChange({ ...data, method: m.id })}
              className="text-left p-4 rounded-xl cursor-pointer transition-all duration-200"
              style={{
                background: data.method === m.id ? `${m.color}10` : 'rgba(255,255,255,0.02)',
                border: data.method === m.id ? `1px solid ${m.color}50` : '1px solid rgba(255,255,255,0.07)',
                boxShadow: data.method === m.id ? `0 0 16px ${m.color}20` : 'none',
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${m.color}15` }}>
                  <i className={`${m.icon} text-sm`} style={{ color: m.color }} />
                </div>
                <div
                  className="w-4 h-4 rounded-full border-2 flex items-center justify-center ml-auto"
                  style={{ borderColor: data.method === m.id ? m.color : 'rgba(255,255,255,0.2)' }}
                >
                  {data.method === m.id && (
                    <div className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                  )}
                </div>
              </div>
              <p className="font-orbitron font-bold text-xs text-white mb-0.5">{m.label}</p>
              <p className="font-mono text-[10px] mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{m.desc}</p>
              <p className="font-mono text-xs font-bold" style={{ color: m.color }}>{m.price}</p>
              <p className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>{m.freeFrom}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Contact info */}
      <div>
        <h3 className="font-orbitron font-bold text-sm text-white mb-4">Контактные данные</h3>
        <div className="space-y-3">
          <div className="flex gap-3">
            <Field label="Имя" value={data.firstName} onChange={set('firstName')} placeholder="Иван" required half />
            <Field label="Фамилия" value={data.lastName} onChange={set('lastName')} placeholder="Иванов" required half />
          </div>
          {errors.firstName && <p className="font-mono text-xs" style={{ color: '#ff2d78' }}>{errors.firstName}</p>}
          <div className="flex gap-3">
            <Field label="Телефон" value={data.phone} onChange={set('phone')} placeholder="+7 (999) 000-00-00" type="tel" required half />
            <Field label="Email" value={data.email} onChange={set('email')} placeholder="ivan@mail.ru" type="email" required half />
          </div>
          {(errors.phone || errors.email) && (
            <p className="font-mono text-xs" style={{ color: '#ff2d78' }}>{errors.phone || errors.email}</p>
          )}
        </div>
      </div>

      {/* Address */}
      {(data.method === 'courier' || data.method === 'post') && (
        <div>
          <h3 className="font-orbitron font-bold text-sm text-white mb-4">Адрес доставки</h3>
          <div className="space-y-3">
            <Field label="Город" value={data.city} onChange={set('city')} placeholder="Москва" required />
            {errors.city && <p className="font-mono text-xs" style={{ color: '#ff2d78' }}>{errors.city}</p>}
            <Field label="Улица, дом" value={data.address} onChange={set('address')} placeholder="ул. Ленина, д. 1" required />
            {errors.address && <p className="font-mono text-xs" style={{ color: '#ff2d78' }}>{errors.address}</p>}
            <Field label="Квартира / офис" value={data.apartment} onChange={set('apartment')} placeholder="кв. 42" />
          </div>
        </div>
      )}

      {/* Pickup point */}
      {data.method === 'pickup' && (
        <div>
          <h3 className="font-orbitron font-bold text-sm text-white mb-4">Пункт выдачи</h3>
          <div className="space-y-2">
            {PICKUP_POINTS.map(pt => (
              <button
                key={pt}
                onClick={() => onChange({ ...data, pickupPoint: pt })}
                className="w-full text-left flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200"
                style={{
                  background: data.pickupPoint === pt ? 'rgba(57,255,20,0.06)' : 'rgba(255,255,255,0.02)',
                  border: data.pickupPoint === pt ? '1px solid rgba(57,255,20,0.3)' : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  className="w-4 h-4 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center"
                  style={{ borderColor: data.pickupPoint === pt ? '#39ff14' : 'rgba(255,255,255,0.2)' }}
                >
                  {data.pickupPoint === pt && <div className="w-2 h-2 rounded-full" style={{ background: '#39ff14' }} />}
                </div>
                <span className="font-mono text-xs" style={{ color: data.pickupPoint === pt ? '#fff' : 'rgba(255,255,255,0.5)' }}>{pt}</span>
              </button>
            ))}
          </div>
          {errors.pickupPoint && <p className="font-mono text-xs mt-2" style={{ color: '#ff2d78' }}>{errors.pickupPoint}</p>}
        </div>
      )}

      {/* Comment */}
      <div>
        <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Комментарий к заказу</label>
        <textarea
          value={data.comment}
          onChange={e => set('comment')(e.target.value)}
          placeholder="Позвоните за 30 минут до доставки..."
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none transition-all resize-none"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
          onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
        />
      </div>

      <button
        onClick={handleNext}
        className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300"
        style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.4)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255,45,120,0.6)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,45,120,0.4)'; }}
      >
        Перейти к оплате
        <i className="ri-arrow-right-line" />
      </button>
    </div>
  );
};

export default DeliveryForm;
